import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { apiFetch, callApi } from '../api';
import Modal from '../components/Modal';
import InfoTip from '../components/InfoTip';
import { useConfirm } from '../components/ConfirmDialog';
import {
  Badge, EmptyState, Field, Icon, PageHeader, TableSkeleton,
} from '../components/ui';

// ── SNS Alarm admin ──────────────────────────────────────────────────────────
// One card: the email addresses subscribed to the CloudWatch scaling-alarm SNS
// topic. Add an email (SNS sends a one-time confirmation link — the row shows
// "pending" until the recipient clicks it), or remove a confirmed one.

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// Until the recipient clicks the link, SNS reports the subscription's ARN as the sentinel
// "PendingConfirmation" (services/sns_alarms.py), which the API maps to 'pending'. A status
// this page has never heard of renders as itself, neutral, rather than disappearing.
const STATUS = {
  confirmed: { tone: 'ok', label: 'Confirmed' },
  pending: { tone: 'warn', label: 'Pending confirmation' },
};

function SubscriptionStatus({ status }) {
  const s = STATUS[status] || { tone: 'neutral', label: status || '—' };
  return <Badge tone={s.tone} dot>{s.label}</Badge>;
}

// ── Add Email Modal ──────────────────────────────────────────────────────────
function AddEmailModal({ onSubmit, onCancel }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async () => {
    const value = email.trim();
    if (!EMAIL_RE.test(value)) {
      toast.error('Enter a valid email address');
      setError('Enter a valid email address, like ops@example.com.');
      return;
    }
    setBusy(true);
    try {
      await onSubmit(value);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={busy ? undefined : onCancel}
      title="Add alarm email"
      description="AWS sends a confirmation link to this address."
      maxWidth={460}
    >
      <Field
        label="Email address"
        htmlFor="sns-add-email"
        required
        error={error}
        hint={(
          <>
            The subscription stays <strong>pending</strong> until the recipient clicks the
            link, and only then starts receiving alarm email.
          </>
        )}
        className="mb-0"
      >
        <input
          id="sns-add-email"
          className="form-input"
          type="email"
          value={email}
          autoFocus
          autoComplete="email"
          inputMode="email"
          spellCheck={false}
          onChange={(e) => { setEmail(e.target.value); if (error) setError(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter' && !busy) handleSubmit(); }}
          placeholder="ops@example.com"
        />
      </Field>
      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" disabled={busy} onClick={onCancel}>Cancel</button>
        <button type="button" className="btn btn-primary" disabled={busy} onClick={handleSubmit}>
          {busy ? <><span className="spinner-sm" /> Subscribing…</> : 'Subscribe'}
        </button>
      </div>
    </Modal>
  );
}

function SnsAlarm() {
  const [subs, setSubs] = useState([]);
  const [topic, setTopic] = useState('');
  const [loading, setLoading] = useState(true);
  // True once the first request has settled, success or not. It separates the first load
  // (skeleton) from a refresh (the table stays put and the Refresh button spins), so adding
  // or removing an address does not blank the list under the cursor.
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [actionBusy, setActionBusy] = useState({});
  const [confirm, confirmDialog] = useConfirm();

  const fetchSubs = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await apiFetch('/api/sns-alarms/subscriptions');
      if (resp.ok) {
        const data = await resp.json();
        setSubs(data.subscriptions || []);
        setTopic(data.topic || '');
        setLoadError(null);
      } else {
        const err = await resp.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to load subscriptions');
        setLoadError(typeof err.detail === 'string' ? err.detail : 'Failed to load subscriptions');
      }
    } catch {
      toast.error('Failed to load subscriptions');
      setLoadError('Failed to load subscriptions');
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, []);

  useEffect(() => { fetchSubs(); }, [fetchSubs]);

  const handleAdd = async (email) => {
    const data = await callApi('/api/sns-alarms/subscriptions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    }, { successMsg: `Confirmation email sent to ${email}`, errorMsg: 'Failed to subscribe' });
    if (data) {
      setShowAdd(false);
      fetchSubs();
    }
  };

  const handleRemove = async (row) => {
    if (!await confirm({
      title: `Remove ${row.email}?`,
      body: (
        <>
          <p>It stops receiving alarm email.</p>
          <p>You can add it back at any time; AWS then asks the recipient to confirm again.</p>
        </>
      ),
      confirmLabel: 'Remove address',
    })) return;
    setActionBusy((p) => ({ ...p, [row.email]: true }));
    const data = await callApi('/api/sns-alarms/subscriptions', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subscription_arn: row.subscription_arn }),
    }, { successMsg: `Removed ${row.email}`, errorMsg: 'Failed to remove' });
    setActionBusy((p) => ({ ...p, [row.email]: false }));
    if (data) fetchSubs();
  };

  const openAdd = () => setShowAdd(true);
  const confirmedCount = subs.filter((s) => s.status === 'confirmed').length;
  const pendingCount = subs.filter((s) => s.status === 'pending').length;

  const retryButton = (
    <button type="button" className="btn btn-secondary btn-sm" onClick={fetchSubs} disabled={loading}>
      {loading ? <span className="spinner-sm" /> : <Icon name="refresh" />}
      Try again
    </button>
  );

  let body;
  if (!loaded) {
    body = <TableSkeleton rows={3} cols={4} label="Loading subscriptions" />;
  } else if (subs.length === 0 && loadError) {
    // Without this the failure fell through to the empty state, which told the admin that
    // nobody was subscribed when the truth was that the list could not be read.
    body = (
      <EmptyState icon="alertCircle" title="The subscriptions did not load" action={retryButton}>
        The server said <code>{loadError}</code>. An empty list here does not mean nobody is
        subscribed.
      </EmptyState>
    );
  } else if (subs.length === 0) {
    body = (
      <EmptyState
        icon="bell"
        title="No alarm emails yet"
        action={(
          <button type="button" className="btn btn-primary" onClick={openAdd}>
            <Icon name="plus" /> Add the first address
          </button>
        )}
      >
        Add an address to start receiving alarm email. AWS asks the recipient to confirm it
        first.
      </EmptyState>
    );
  } else {
    body = (
      <div className="table-container">
        <table className="table table-stack">
          <thead>
            <tr>
              <th>Endpoint</th>
              <th className="hide-sm">Protocol</th>
              <th>Status</th>
              <th><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {subs.map((row) => {
              const isPending = row.status === 'pending';
              const busy = !!actionBusy[row.email];
              return (
                <tr key={row.subscription_arn + row.email}>
                  <td data-label="Endpoint">
                    <span className="cell-primary mono break">{row.email}</span>
                  </td>
                  {/* The API lists email subscriptions only (services/sns_alarms.py skips
                      every other protocol), so this reads "Email" today. It shows
                      `protocol` if the payload ever carries one, rather than asserting a
                      value it was not sent. */}
                  <td data-label="Protocol" className="hide-sm">
                    <Badge tone="outline" icon="mail">
                      {row.protocol && row.protocol !== 'email' ? row.protocol : 'Email'}
                    </Badge>
                  </td>
                  <td data-label="Status">
                    <div className="stack gap-1 items-start">
                      <SubscriptionStatus status={row.status} />
                      {isPending && (
                        <span className="text-xs text-muted">Expires in about 3 days if not confirmed</span>
                      )}
                    </div>
                  </td>
                  <td data-label="Actions">
                    <div className="row row-end">
                      <button
                        type="button"
                        className="btn btn-danger-soft btn-sm"
                        disabled={busy || isPending}
                        title={isPending
                          ? 'Pending subscriptions auto-expire and cannot be removed manually'
                          : undefined}
                        aria-label={`Remove ${row.email}`}
                        onClick={() => handleRemove(row)}
                      >
                        {busy ? <span className="spinner-sm" /> : <Icon name="trash" />}
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div>
      {/* Title, tip and both actions sit in the page header. This page had the name
          twice ("SNS alarm" over "SNS alarm emails") and the lede twice, the second
          copy differing only in whether the alarms were called CloudWatch or
          scaling — so the two readings disagreed about what fires the email. */}
      <PageHeader
        description="Who gets emailed when this environment's CloudWatch scaling alarms fire."
        actions={(
          <>
            <button type="button" className="btn btn-secondary" onClick={fetchSubs} disabled={loading}>
              {loading && loaded ? <span className="spinner-sm" /> : <Icon name="refresh" />}
              Refresh
            </button>
            <button type="button" className="btn btn-primary" onClick={openAdd}>
              <Icon name="plus" /> Add email
            </button>
          </>
        )}
      >
        <h1 className="page-title">
          SNS alarm
          <InfoTip label="About alarm emails" width={350}>
            <p>
              Addresses on this environment&apos;s CloudWatch scaling alarms
              {topic ? <>, topic <code>{topic}</code></> : null}.
            </p>
            <p>
              Adding one sends an AWS confirmation link. Until it is clicked the row stays{' '}
              <strong>pending</strong> and receives nothing.
            </p>
            <p>
              A pending row cannot be removed here: AWS gives it no identifier until confirmed.
              It clears itself after about three days.
            </p>
          </InfoTip>
          {loaded && !loadError && <Badge tone="neutral" size="lg">{subs.length}</Badge>}
        </h1>
      </PageHeader>

      {/* A refresh that fails while an older list is on screen: keep the list, say it may
          be stale. A first load that fails is handled inside the card instead. */}
      {loaded && loadError && subs.length > 0 && (
        <div className="notice notice-warn" role="status">
          <Icon name="alert" />
          <div className="notice-body">
            <div className="notice-title">The list could not be refreshed</div>
            The server said <code>{loadError}</code>. What is shown below may be out of date.
          </div>
          {retryButton}
        </div>
      )}

      <div className="card card-flush">
        <div className="card-header">
          <div className="min-w-0">
            <h2 className="card-title">Alarm emails</h2>
            <p className="card-subtitle">
              Email subscriptions on {topic ? <code>{topic}</code> : 'the alarms topic'}.
              Subscriptions on other protocols are not shown here.
            </p>
          </div>
          {loaded && subs.length > 0 && (
            <div className="card-actions">
              <Badge tone="ok" dot>{confirmedCount} confirmed</Badge>
              {pendingCount > 0 && <Badge tone="warn" dot>{pendingCount} pending</Badge>}
            </div>
          )}
        </div>
        {body}
      </div>

      {showAdd && (
        <AddEmailModal onSubmit={handleAdd} onCancel={() => setShowAdd(false)} />
      )}

      {confirmDialog}
    </div>
  );
}

export default SnsAlarm;
